import { createSignal, createEffect, createRoot, batch } from "solid-js";
import type { AComponent } from "aframe";
import type { ActionButtonType, Training } from "./ExecutableActionTypes";
import type { Banner, ObjectInfo, SceneElements, TeleportableZone } from "./SceneElementsTypes";
import { createStore, produce, reconcile } from "solid-js/store";
import type { RoomConnectionConfig } from "./RoomConnectionTypes";
import { createBroadcastStore } from "./broadcastStore";
import type { User } from "../../server/lib/auth";

export const [session, setSession] = createStore<{
  user: User | null;
}>({
  user: null,
});

export const [tenantId, setTenantId] = createSignal("");

// Indicator color convention in rooms (furnace synoptic panel + 3D baco
// spheres). Default (false): red = started/flame present, green = stopped (the
// customer's plant convention). true = green means running (international
// convention). Set by the ?reverseIndicatorColors URL param: hand-written, or
// appended by the training plan view when the student's class (team.config)
// defines the setting — the plan's assigned classes disambiguate a user who
// belongs to several classes with different conventions.
const urlReverseIndicatorColors =
  typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("reverseIndicatorColors") : null;
export const [reverseIndicatorColors, setReverseIndicatorColors] = createSignal(urlReverseIndicatorColors === "true");
export const [interfaceHidden, setInterfaceHidden] = createSignal(false);
export const [isReconnecting, setIsReconnecting] = createSignal(false);

export const [headsetConnected, setHeadsetConnected] = createSignal(false);
export const [supportsVRSession, setSupportsVRSession] = createSignal(false);
export const [supportsARSession, setSupportsARSession] = createSignal(false);

export const [appName, setAppName] = createSignal("AlloCola");
export const [sceneTitle, setSceneTitle] = createSignal("");
export const [sceneModel, setSceneModel] = createSignal("");
export const [branding, setBranding] = createSignal("");
export const [sceneElements, setSceneElements] = createSignal<SceneElements>({
  sceneTitle: "",
  sceneModel: "",
});
export const [baseURLForAssets, setBaseURLForAssets] = createSignal("");
export const [banner, setBanner] = createSignal<Banner | null>(null);
export const [objectsInfos, setObjectsInfos] = createSignal<Array<ObjectInfo>>([]);
export const [entityInfoPanels, setEntityInfoPanels] = createStore<{ list: ObjectInfo[] }>({ list: [] });

export function registerEntityInfoPanel(id: string, info: ObjectInfo) {
  const idx = entityInfoPanels.list.findIndex((p) => p.id === id);
  if (idx >= 0) {
    setEntityInfoPanels("list", idx, reconcile(info));
  } else {
    setEntityInfoPanels(
      produce((state) => {
        state.list.push(info);
      })
    );
  }
}

export function unregisterEntityInfoPanel(id: string) {
  setEntityInfoPanels(
    produce((state) => {
      const idx = state.list.findIndex((p) => p.id === id);
      if (idx >= 0) {
        state.list.splice(idx, 1);
      }
    })
  );
}

export const [actionButtons, setActionButtons] = createSignal<Array<ActionButtonType>>([]);
export const DEFAULT_TRAINING: Training = {
  ui: "advanced",
  options: { sectionButtons: true, prevNextButtons: true },
  label: "",
  sections: [],
};
export const [training, setTraining, setTrainingBroadcastChannel] = createBroadcastStore<Training>(
  structuredClone(DEFAULT_TRAINING)
);
export const [trainingState, setTrainingState] = createStore<Record<string, any>>({ score: 0 });

// Entity-local ("scope: local") state. A scope:local bind-variable writes into
// a per-scope-root store instead of the global trainingState, and a bind component
// reads it through the `localState.myvar` syntax. The scope root is the template
// instance the entity belongs to, so two instances of the same template can reuse
// the same variable name without clobbering each other — no per-instance renaming.
//
// The scope root is the nearest ancestor-or-self carrying data-template-id (the
// template instance root). When the entity isn't inside a template instance it
// falls back to the entity itself, so localState then means "this entity only".
const localStateStores = new WeakMap<Element, [Record<string, any>, (key: string, value: any) => void]>();

/** Find the scope root for an element: nearest ancestor-or-self with data-template-id,
 *  falling back to the element itself when it isn't inside a template instance. */
export function resolveScopeRoot(el: Element): Element {
  let cur: Element | null = el;
  while (cur) {
    if (cur.hasAttribute?.("data-template-id")) return cur;
    cur = cur.parentElement;
  }
  return el;
}

/** Get (creating if asked) the reactive [store, setStore] for a scope root element. */
export function getLocalStateStore(scopeRoot: Element, create = false) {
  let entry = localStateStores.get(scopeRoot);
  if (!entry && create) {
    entry = createStore<Record<string, any>>({});
    localStateStores.set(scopeRoot, entry);
  }
  return entry;
}

/** Read-side accessor: resolve the local store an entity's bind should read from.
 *  Pre-creates it so a bind that runs before its bind-variable still subscribes to
 *  the (initially empty) store and re-runs once the variable is set. */
export function getLocalState(el: Element): Record<string, any> {
  return getLocalStateStore(resolveScopeRoot(el), true)![0];
}

/** Write-side helper: set/clear an entity-local variable on the element's scope root. */
export function setLocalState(el: Element, key: string, value: any) {
  getLocalStateStore(resolveScopeRoot(el), true)![1](key, value);
}
export const [rawTraining, setRawTraining] = createSignal<Training>(structuredClone(DEFAULT_TRAINING));
export const [contentLanguage, setContentLanguage] = createSignal("en");
export const [currentTrainingSection, setCurrentTrainingSection] = createSignal(-1);
export const [currentTrainingLesson, setCurrentTrainingLesson] = createSignal(-1);
export const currentOpenedPanel: { setState: ((newState: boolean) => void) | null } = { setState: null };
export const [teleportableZones, setTeleportableZones] = createSignal<Array<TeleportableZone>>([]);
export const [isInVR, setIsInVR] = createSignal(false);
export const [isOpened360, setIsOpened360] = createSignal(false);
export const [currentImage, setCurrentImage] = createSignal("");
export const [loadingPercent, setLoadingPercent] = createSignal(0);
export const [muteAllSoundsExceptTTS, setMuteAllSoundsExceptTTS] = createSignal(false);
export const [showRestartExperienceButton, setShowRestartExperienceButton] = createSignal(false);
export const [previousPlace, setPreviousPlace] = createSignal("");
export const [currentPlace, setCurrentPlace] = createSignal("");
export const [showPreviousPlaceButton, setShowPreviousPlaceButton] = createSignal(false);

export const [clientId, setClientId] = createSignal("");
const ls = typeof localStorage !== "undefined" ? localStorage : undefined; // to be able to run tests without localStorage
export const [pseudo, setPseudo] = createSignal(ls?.getItem("pseudo") || "");
export const [pseudoWithoutDollar, setPseudoWithoutDollar] = createSignal(ls?.getItem("pseudo") || "");
// instead of createMemo, we use a signal and effect in App component to avoid the warning
// computations created outside a `createRoot` or `render` will never be disposed
// export const pseudoWithoutDollar = createMemo(() => {
//   return pseudo().replace("$", "");
// });
export const [userCompany, setUserCompany] = createSignal("");
export const [userRole, setUserRole] = createSignal("");
export const [userEmail, setUserEmail] = createSignal("");
export const [userPhone, setUserPhone] = createSignal("");

export const [aiChatOpen, setAIChatOpen] = createSignal(false);
export const [aiAgent, setAIAgent] = createSignal<typeof AComponent | null>(null);
export const [aiAgentMuted, setAIAgentMuted] = createSignal(false);
export const [aiAgentPersonality, setAIAgentPersonality] = createSignal("");
export const [pendingMessage, setPendingMessage] = createSignal("");

export const isStaff = (speaker: string) =>
  speaker === "Vincent" ||
  speaker === "Romain" ||
  speaker === "Ranim" ||
  speaker === "Florian" ||
  speaker.startsWith("Alice") ||
  speaker.startsWith("Eric") ||
  speaker.startsWith("Éric");

export const currentUserIsStaff = () => {
  const speaker = pseudoWithoutDollar().split(" ")[0];
  return isStaff(speaker);
};

export const userIsAllowedToUseAI = (username: string) => {
  if (isStaff(username)) return true;
  const agent = aiAgent();
  if (!agent) return false;
  return agent.data.enabledForAll;
};

export const [userIsAdmin, setUserIsAdmin] = createSignal(false);
export const [isOrganizationSwitching, setIsOrganizationSwitching] = createSignal(false);

export enum Step {
  NOT_INITIALIZED = "NOT_INITIALIZED",
  INIT = "INIT",
  SCENE_NOT_FOUND = "SCENE_NOT_FOUND",
  // SELECT_SCENE = "SELECT_SCENE",
  BEFORE_MIC_SELECTION = "BEFORE_MIC_SELECTION",
  MIC_SELECTION = "MIC_SELECTION",
  AVATAR_SELECTION = "AVATAR_SELECTION",
  ENTERED_SINGLE_USER = "ENTERED_SINGLE_USER",
  ENTERED_MULTI_USER = "ENTERED_MULTI_USER",
  LEFT_ROOM = "LEFT_ROOM",
}
export const [step, setStep] = createSignal<Step>(Step.NOT_INITIALIZED);

export const [roomConnectionConfig, setRoomConnectionConfig] = createSignal<RoomConnectionConfig | null>(null);

// Scenario signals (set when visiting a room via /r/:roomId/s/:scenarioLinkId)
export const [currentRoomId, setCurrentRoomId] = createSignal<string | null>(null);
// Training plan context when the scenario was launched from a plan view (?plan=<planId>)
export const [currentTrainingPlanId, setCurrentTrainingPlanId] = createSignal<string | null>(null);
export const [currentScenarioLinkId, setCurrentScenarioLinkId] = createSignal<string | null>(null);
export const [currentScenarioId, setCurrentScenarioId] = createSignal<string | null>(null);
// Scene variant (room_variants) the current visit runs under: the one the
// scenario link is assigned to, or the ?variant= URL param on a plain room
// visit. Not to be confused with a "scenario variant" (a room_scenarios link).
export const [currentSceneVariantId, setCurrentSceneVariantId] = createSignal<string | null>(null);
export const [currentScenarioVariables, setCurrentScenarioVariables] = createSignal<Record<string, any> | null>(null);
// Variables inherited from scenario defaults + room + variant (without link overrides)
export const [currentScenarioInheritedVariables, setCurrentScenarioInheritedVariables] = createSignal<Record<
  string,
  any
> | null>(null);
// Raw per-link variable overrides (what the variant settings editor edits and saves)
export const [currentScenarioLinkVariables, setCurrentScenarioLinkVariables] = createSignal<Record<string, any> | null>(
  null
);
export const [hasLegacyTraining, setHasLegacyTraining] = createSignal(false);

// Disposer for the built-in signal sync effects
let builtinSyncDisposer: (() => void) | null = null;

/**
 * Initialize trainingState from scenario variables and sync built-in signals.
 * Scenario variables serve as the initial state. Built-in signals (isInVR, isModerator, isOpened360)
 * are synced reactively so expressions like "score > 5 && isInVR" work.
 *
 * @param variables - Scenario variables (key-value pairs) to use as initial state
 */
export function initializeTrainingState(variables: Record<string, any> | null) {
  // Dispose previous sync effects
  if (builtinSyncDisposer) {
    builtinSyncDisposer();
    builtinSyncDisposer = null;
  }

  // Reset state with score:0 + scenario variables
  batch(() => {
    setTrainingState(
      produce((state: Record<string, any>) => {
        // Don't remove existing keys, the bind-variable components are initialized before
        // the initializeTrainingState call, so removing the keys would trigger changes
        // with undefined on bind components.
        // Set defaults + scenario variables
        state.score = 0;
        if (variables) {
          for (const [key, value] of Object.entries(variables)) {
            state[key] = value;
          }
        }
      })
    );
  });

  // Sync built-in signals into the store reactively
  createRoot((dispose) => {
    builtinSyncDisposer = dispose;

    createEffect(() => {
      setTrainingState("isInVR", isInVR());
    });
    createEffect(() => {
      setTrainingState("isModerator", currentUserIsStaff());
    });
    createEffect(() => {
      setTrainingState("isOpened360", isOpened360());
    });
  });
}
